import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface PaymentSuccessPopupProps {
  open: boolean;
  onClose?: () => void;
  orderId?: string;
  amount?: number;
}

const PaymentSuccessPopup: React.FC<PaymentSuccessPopupProps> = ({
  open,
  onClose,
  orderId,
  amount,
}) => {
  const navigate = useNavigate();

  const notifyOpener = () => {
    try {
      if (window.opener && !window.opener.closed && orderId) {
        window.opener.postMessage(
          { type: 'PAYMENT_SUCCESS', orderId },
          window.location.origin
        );
      }
    } catch {
      // ignore
    }
  };

  const handleClose = () => {
    notifyOpener();
    if (onClose) {
      onClose();
    } else {
      // Default: navigate to home or close the popup window
      if (window.opener) {
        // If opened in a popup, close it
        window.close();
      } else {
        navigate('/');
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 5,
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(18px)',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          boxShadow: '0 18px 60px rgba(15, 23, 42, 0.18)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
            color: 'text.secondary',
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            p: { xs: 3.5, sm: 5 },
            textAlign: 'center',
            background:
              'radial-gradient(circle at top, rgba(16,185,129,0.14) 0%, rgba(255,255,255,1) 55%, rgba(255,255,255,1) 100%)',
          }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              type: 'spring',
              stiffness: 200,
              damping: 15,
            }}
          >
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(16,185,129,1) 0%, rgba(34,197,94,1) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                boxShadow: '0 18px 40px rgba(16,185,129,0.28)',
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 60, color: 'white' }} />
            </Box>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 'bold',
                mb: 2,
                color: 'text.primary',
                background: 'linear-gradient(135deg, rgba(16,185,129,1) 0%, rgba(34,197,94,1) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Payment Successful!
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', mb: 4 }}
            >
              Your payment is confirmed. You can safely continue — we’ll publish your listing automatically.
            </Typography>
          </motion.div>

          {(orderId || amount) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.04)',
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  mb: 4,
                }}
              >
                {amount && (
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      mb: orderId ? 1 : 0,
                    }}
                  >
                    ${amount.toFixed(2)} USD
                  </Typography>
                )}
                {orderId && (
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', fontFamily: 'monospace' }}
                  >
                    Order ID: {orderId}
                  </Typography>
                )}
              </Box>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Button
              variant="contained"
              fullWidth
              onClick={handleClose}
              sx={{
                background: 'linear-gradient(135deg, rgba(16,185,129,1) 0%, rgba(34,197,94,1) 100%)',
                py: 1.6,
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 900,
                fontSize: '1.05rem',
                letterSpacing: 0.2,
                boxShadow: '0 14px 34px rgba(16,185,129,0.28)',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(5,150,105,1) 0%, rgba(22,163,74,1) 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 18px 44px rgba(16,185,129,0.32)',
                },
                '&:active': {
                  transform: 'translateY(0px) scale(0.99)',
                },
                transition: 'all 0.18s ease',
              }}
            >
              Continue
            </Button>
          </motion.div>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentSuccessPopup;

